"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createHealthApi = void 0;
const error_interfaces_1 = require("../types/error.interfaces");
const subject_1 = require("../utilities/subject");
const format_string_1 = require("../utilities/format-string");
/**
 * Create endpoints manager's health API and health tracker
 */
const createHealthApi = () => {
    /**
     * Endpoints health records store info about endpoint health
     */
    const _endpointHealthMap = new Map();
    /**
     * Start tracking endpoint health by creating a new health record in health map
     * @param serviceName service/set name
     * @param endpointName - [string] - endpoint name
     * @param isHealthyByDefault - initial health of endpoint
     * @returns new health record
     */
    const _startTrackingEndpointHealth = (serviceName, endpointName) => {
        const health = {
            serviceName,
            endpointName,
            isHealthy: undefined,
        };
        const healthRecord = {
            responseHistory: [],
            healthSubject: new subject_1.Subject(health),
        };
        _endpointHealthMap.set(_getHealthMapKey(serviceName, endpointName), healthRecord);
        return healthRecord;
    };
    /**
     * Get endpoint health record or create a new one
     * @param serviceName - [string] - service/set name
     * @param endpointName  - [string] - endpoint name
     */
    const _getEndpointHealthRecord = (serviceName, endpointName) => _endpointHealthMap.get(_getHealthMapKey(serviceName, endpointName)) ||
        _startTrackingEndpointHealth(serviceName, endpointName);
    const _getEndpointHealthObservable = (serviceName, endpointName) => {
        if (!serviceName || !endpointName) {
            return undefined;
        }
        const healthRecord = _getEndpointHealthRecord(serviceName, endpointName);
        return healthRecord.healthSubject.observable;
    };
    /**
     * Add response to response history
     * @param healthRecord - health record to which response history to add the response
     * @param requestResult - the response to add
     * @param historyLength - response history length
     */
    const _addResponseToResponseHistory = (healthRecord, requestResult, historyLength) => {
        healthRecord.responseHistory.push(requestResult);
        if (healthRecord.responseHistory.length > historyLength) {
            healthRecord.responseHistory.shift();
        }
    };
    /**
     * Update endpoint health information
     * @param endpointName - [string] - endpoint name
     * @param serviceName - [string] - endpoint set name
     * @param response - [Status | ErrorReason] - http response status or error reason
     * @param healthPolicy - health policy
     */
    const _updateEndpointHealth = (endpointName, serviceName, requestResult, healthPolicy) => {
        const healthRecord = _getEndpointHealthRecord(serviceName, endpointName);
        _addResponseToResponseHistory(healthRecord, requestResult, healthPolicy.historyLength || 5);
        const isHealthy = healthPolicy.isHealthy([...healthRecord.responseHistory]);
        if (healthRecord.healthSubject.value.isHealthy !== isHealthy) {
            const currentHealth = {
                serviceName,
                endpointName,
                isHealthy,
            };
            healthRecord.healthSubject.onNext(currentHealth);
        }
    };
    return {
        trackHealth: (send, serviceName, healthPolicy) => _healthWrapper(send, _updateEndpointHealth, serviceName, _getHealthPolicyWithDefaults(healthPolicy)),
        observeEndpointHealth: _getEndpointHealthObservable,
        getEndpointHealth: (serviceName, endpointName) => _getEndpointHealth(serviceName, endpointName, _endpointHealthMap),
        dumpState: () => _printEndpointsState(_endpointHealthMap),
    };
};
exports.createHealthApi = createHealthApi;
/**
 * Get health policy where undefined optional fields are replaced with defaults
 * @param healthPolicy - endpoint model's health policy
 */
const _getHealthPolicyWithDefaults = (healthPolicy) => ({
    ..._defaultHealthPolicy,
    ...healthPolicy,
});
/**
 * Get endpoint health from the health map
 * @param serviceName
 * @param endpointName
 */
const _getEndpointHealth = (serviceName, endpointName, endpointsHealthMap) => {
    const endpointHealth = endpointsHealthMap.get(_getHealthMapKey(serviceName, endpointName))?.healthSubject.value;
    return endpointHealth && { ...endpointHealth }; // a copy of endpoint health should be returned
};
/**
 * Get health map key for service-endpoint pair
 * @param serviceName service/set name
 * @param endpointName endpoint name
 */
const _getHealthMapKey = (serviceName, endpointName) => `${serviceName}_${endpointName}`;
/**
 * Health tracker interceptor injected into http Send stack
 */
const _healthWrapper = (send, updateHealthState, serviceName, healthPolicy) => async (request) => send(request)
    .then((response) => {
    updateHealthState(request.apiName || "", serviceName, response.status, healthPolicy);
    return response;
})
    .catch((error) => {
    updateHealthState(request.apiName || "", serviceName, error.reason, healthPolicy);
    throw error;
});
/**
 * Default endpoint health policy is used when no policy is defined
 */
const _defaultHealthPolicy = {
    /**
     * Endpoint is considered unhealthy when last response satisfies the following:
     * - failed to fetch error (network error)
     * - required status failed (see 'allowedResponses' setting in IEndpointModelBase)
     * - http error is rejected (see 'rejectHttpError' setting in IEndpointModelBase)
     * @param endpointResponses - a history of last responses and errors
     */
    isHealthy: (endpointResponses) => {
        if (endpointResponses && endpointResponses.length > 0) {
            const lastStatusOrErrorReason = endpointResponses[endpointResponses.length - 1]; // the last is the most recent
            if (!isStatus(lastStatusOrErrorReason)) {
                // cancellation is not a failed endpoint, thus returning Healthy for it
                if (lastStatusOrErrorReason !== error_interfaces_1.ErrorReason.RequestCancelled) {
                    return false; // some error has happened
                }
            }
        }
        // all statuses which were not converted to errors are considered a healthy state
        return true;
    },
    /**
     * The length of endpoint responses lookback
     */
    historyLength: 5,
};
function isStatus(s) {
    return typeof s === "number";
}
/**
 * Print health of all endpoints to a string
 */
const _printEndpointsState = (endpointsHealthMap) => {
    let result = "";
    endpointsHealthMap.forEach((record) => {
        result = `${result}${JSON.stringify(record.healthSubject.value)}`;
    });
    return result ? (0, format_string_1.unleakString)(result) : "";
};
